import React from "react";
import { IconPencil, IconQuestionMark, IconTrash } from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UserURL } from "@/functions";
import { IPages, IUserData } from "@/types";
import { toast } from "sonner";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

interface EditPagePopoverProps {
    page: IPages;
    isActive: boolean;
    user: IUserData;
    pages: IPages[];
    setNewPages: React.Dispatch<React.SetStateAction<IPages[]>>;
    impersonate?: boolean;
}

export const EditPagePopover: React.FC<EditPagePopoverProps> = ({
    page,
    isActive,
    user,
    pages,
    setNewPages,
    impersonate,
}) => {
    const [editPageName, setEditPageName] = React.useState("");
    const [editPageUrl, setEditPageUrl] = React.useState("");
    const [editPageEnabled, setEditPageEnabled] = React.useState(page.enabled);
    const [editPageVisible, setEditPageVisible] = React.useState(page.visible);
    const currentPage = page;
    const editPage = async (
        page: IPages,
        enabled: boolean,
        visible: boolean
    ) => {
        const loading = toast.loading("Updating page...");
        const res = await fetch("/api/admin/pages/edit", {
            method: "POST",
            body: JSON.stringify({
                id: page.id,
                name: editPageName,
                url: editPageUrl,
                enabled: enabled,
                sort: page.sort,
                old_pages: pages,
                impersonate: impersonate ? user.id : null,
                visible: visible,
            }),
        });
        const data = await res.json();
        if (!data.error) {
            const pageUrl = `/admin/content/${editPageUrl}`;
            toast.success("Updated", { id: loading });
            if (isActive) {
                window.location.href = pageUrl;
            } else {
                const newPages = pages.map((p) => {
                    if (p.id === page.id) {
                        return {
                            ...p,
                            name: editPageName,
                            slug: editPageUrl,
                            enabled: enabled,
                            visible: visible,
                        };
                    }
                    return p;
                });
                setNewPages(newPages);
            }
        } else {
            if (data?.error == "page slug not allowed") {
                toast.error("page slug not allowed", { id: loading });
            } else if (data?.error == "page slug already taken") {
                toast.error("page slug already taken", { id: loading });
            } else if (data?.error == "page slug too short") {
                toast.error("page slug too short", { id: loading });
            } else {
                toast.error(
                    "Something went wrong, refresh and try again. If the issue persists, please log out and log back in",
                    {
                        description: JSON.stringify(data),
                        id: loading,
                    }
                );
            }
        }
    };
    const deletePage = async (page: IPages) => {
        toast("Deleting page...");
        const res = await fetch("/api/admin/pages/remove", {
            method: "POST",
            body: JSON.stringify({
                id: page.id,
                old_pages: pages,
                impersonate: impersonate ? user.id : null,
            }),
        });
        const data = await res.json();
        if (!data.error) {
            if (isActive) {
                window.location.href = "/admin/content";
            } else {
                setNewPages(pages.filter((_page) => _page.id !== page.id));
            }
        } else {
            toast.error(
                "Something went wrong, refresh and try again. If the issue persists, please log out and log back in",
                {
                    description: JSON.stringify(data),
                }
            );
        }
    };
    React.useEffect(() => {
        if (currentPage) {
            setEditPageName(currentPage.name);
            setEditPageUrl(currentPage.slug);
        }
    }, [currentPage]);

    if (!currentPage) {
        return null;
    }

    return (
        <Popover>
            <PopoverTrigger asChild>
                {/* ${isExpanded ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"} */}
                <Button
                    className={`group relative flex shrink-0 gap-2 rounded-md bg-accent text-primary transition-all duration-300 hover:bg-primary hover:text-primary-foreground data-[state=open]:bg-primary data-[state=open]:text-primary-foreground
                        
                        
                        `}
                    // size="icon"
                    onClick={() => {
                        setEditPageName(currentPage.name);
                        setEditPageUrl(currentPage.slug);
                    }}
                >
                    <IconPencil size={18} />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="flex w-full min-w-40 flex-col gap-3 bg-popover"
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                <Input
                    label="Page name"
                    className="text-copy bg-muted"
                    variant="card"
                    onChange={(e) => setEditPageName(e.target.value)}
                    value={editPageName}
                />
                <div className="flex items-center">
                    <p className="text-copy text-nowrap rounded-l-md bg-primary py-2 pl-2 pr-0.5">
                        {UserURL(user)}/
                    </p>
                    <Input
                        variant="card"
                        className="text-copy rounded-l-none bg-muted pl-0.5"
                        value={editPageUrl}
                        onChange={(e) =>
                            setEditPageUrl(
                                e.target.value
                                    .replaceAll(" ", "-")
                                    .replaceAll("/", "-")
                                    .toLowerCase()
                            )
                        }
                    />
                </div>
                <Button
                    onClick={() =>
                        editPage(currentPage, editPageEnabled, editPageVisible)
                    }
                >
                    Save
                </Button>
                <Separator />
                <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between gap-1 border border-transparent p-1">
                        <Label variant={"toggle"}>Page visibility</Label>
                        <TooltipProvider>
                            <Tooltip delayDuration={0}>
                                <TooltipTrigger asChild>
                                    <div className="cursor-pointer rounded-md bg-accent p-1">
                                        <IconQuestionMark
                                            size={14}
                                            className=""
                                        />
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent className="z-[9999] bg-accent">
                                    <p>
                                        Show/hide this page from being accessed
                                        by the public
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                        <Switch
                            className="ml-auto"
                            checked={editPageVisible}
                            onCheckedChange={(value) => {
                                setEditPageVisible(value);
                                editPage(currentPage, editPageEnabled, value);
                            }}
                        />
                    </div>
                    <div className="flex items-center justify-between gap-1 border border-transparent p-1">
                        <Label variant={"toggle"}>Navigation visibility</Label>
                        <TooltipProvider>
                            <Tooltip delayDuration={0}>
                                <TooltipTrigger asChild>
                                    <div className="cursor-pointer rounded-md bg-accent p-1">
                                        <IconQuestionMark
                                            size={14}
                                            className=""
                                        />
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent className="z-[9999] bg-accent">
                                    <p>
                                        Show/hide this page on the bottom
                                        navigation and menu
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                        <Switch
                            className="ml-auto"
                            checked={editPageEnabled}
                            onCheckedChange={(value) => {
                                setEditPageEnabled(value);
                                editPage(currentPage, value, editPageVisible);
                            }}
                        />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between gap-1 rounded-md border border-destructive bg-destructive/60 p-1">
                        <Label variant="toggle">Delete page</Label>
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <IconTrash
                                    className="text-error cursor-pointer"
                                    aria-label="Delete"
                                />
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>
                                        Are you sure you want to delete this
                                        page?
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                        This action cannot be undone. This will
                                        permanently delete the page and it's
                                        containing contents.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>
                                        Cancel
                                    </AlertDialogCancel>
                                    <AlertDialogAction
                                        onClick={() => deletePage(currentPage)}
                                    >
                                        Delete
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
};
